/**
 * SurfaceActionBar — `nav.ab`, the bar pinned to the bottom of the surface.
 *
 * Built to `publisher-drop-hub.html` lines 1444-1452.
 *
 * ## Why the primary actions live at the BOTTOM
 *
 * `position: fixed` on the bottom edge, with the surface's name centred in it.
 * The stage already carries the campaign's own CTA — pre-order, wishlist —
 * which is about the TITLE. These are about the viewer's relationship to the
 * surface: join the challenge, follow the team, and what they have earned so
 * far. Those stay reachable at any scroll depth, which a hero CTA cannot be.
 *
 * There is no centre label. It existed to name the surface once the header had
 * scrolled away, and the header is fixed now — so it was repeating what sits
 * permanently 76px above it. It was also `position:absolute; left:50%`, out of
 * flow, so the groups either side could not see it: the moment the right side
 * grew to carry the platform group they printed straight through each other.
 *
 * ## Every group is optional
 *
 * A surface with no actions, no name and no standing renders nothing at all —
 * the component returns null rather than a 62px empty strip across the bottom
 * of the viewport. Same rule as every other section of the spine.
 */

import React from "react";
import {ExternalLink, isExternalHref} from "@/widgets/ExternalLink";

export interface SurfaceActionBarItem {
    id: string;
    label: string;
    /** Drawn in the circular outline. Absent renders the label alone. */
    icon?: React.ReactNode;
    /** Trailing qualifier, e.g. "Tier 2" beside "340 readiness". */
    detail?: string;
    onClick?: () => void;
    href?: string;
    /** The item whose target is currently on screen — the bar's selected state. */
    active?: boolean;
}

export interface SurfaceActionBarLegalLink {
    id: string;
    label: string;
    href?: string;
    /**
     * A CONTROL rather than a document — cookie preferences reopens the consent
     * manager. It is the one item in the legal group that does something rather
     * than goes somewhere, so it renders as a button and is marked.
     */
    isControl?: boolean;
    onClick?: () => void;
}

export interface SurfaceActionBarProps {
    /**
     * The SURFACE's own icon, far left. Not a platform mark: the two corners of
     * this bar say two different things — whose surface this is, and what it
     * runs on. Ours in both corners would make the operator's footer read as
     * ours.
     */
    iconUrl?: string;
    iconAlt?: string;
    /** Left group — what the viewer can DO. */
    actions?: readonly SurfaceActionBarItem[];
    /**
     * What the viewer HAS. Sits WITH the actions, not on the far edge: both are
     * about the viewer, so they read as one run and the gap opens before the
     * platform group instead of in the middle of the bar.
     */
    standing?: readonly SurfaceActionBarItem[];
    /**
     * The platform group, far right. A hub does not get the DevourPlay
     * marketing footer — that one sells the platform, and under an operator's
     * own surface it reads as though the page belongs to us; on a custom domain
     * it is simply wrong. What must still be on every surface we serve is the
     * legal footing and the fact of what runs underneath, and both are small
     * permanent statements rather than navigation — which is what this bar
     * already is.
     */
    platformLogoUrl?: string;
    legal?: readonly SurfaceActionBarLegalLink[];
}

const Item: React.FC<{ item: SurfaceActionBarItem }> = ({item}) => {
    const body =
        <>
            {item.icon && <u>{item.icon}</u>}
            {item.label}
            {item.detail && <em>{item.detail}</em>}
        </>;
    const cls = `sh-abi${item.active ? " sh-abi--on" : ""}`;
    /*
     * Same scheme guard the legal group uses. `href` here is admin-authored
     * (the purchase CTA), so an unvalidated anchor would render a
     * `javascript:` value as a clickable bar entry.
     */
    if (isExternalHref(item.href)) {
        return <ExternalLink className={cls} href={item.href ?? ""}>{body}</ExternalLink>;
    }
    if (item.onClick) {
        return <button type="button" className={cls} aria-current={item.active || undefined} onClick={item.onClick}>{body}</button>;
    }
    /*
     * The right-hand group is status, not action — "Beta access secured" is a
     * statement about the viewer, and there is nowhere for it to go. A span
     * keeps it out of the tab order, where a link with no destination would
     * otherwise sit and do nothing.
     */
    return <span className="sh-abi">{body}</span>;
};

export const SurfaceActionBar: React.FC<SurfaceActionBarProps> = ({
    iconUrl, iconAlt, actions = [], standing = [], platformLogoUrl, legal = [],
}) => {
    const hasPlatform = Boolean(platformLogoUrl) || legal.length > 0;
    if (actions.length === 0 && standing.length === 0 && !hasPlatform) return null;

    return (
        <nav className="sh-ab" aria-label="Surface actions">
            {iconUrl && <img className="sh-ab__mk" src={iconUrl} alt={iconAlt ?? ""} />}

            {actions.map(item => <Item key={item.id} item={item} />)}

            {standing.length > 0 &&
                <span className="sh-ab__r">
                    {standing.map(item => <Item key={item.id} item={item} />)}
                </span>}

            {hasPlatform &&
                <span className="sh-ab__plat">
                    {platformLogoUrl &&
                        <span className="sh-ab__pw">
                            <img src={platformLogoUrl} alt="DevourPlay" />
                        </span>}
                    {legal.length > 0 &&
                        <span className="sh-ab__lg">
                            {legal.map((link, i) =>
                                <React.Fragment key={link.id}>
                                    {i > 0 && <i aria-hidden="true">·</i>}
                                    {link.isControl
                                        ? <button type="button" className="sh-ab__ck" onClick={link.onClick}>
                                            {link.label}
                                        </button>
                                        : isExternalHref(link.href)
                                            /* Off-site legal opens out; in-frame it strands an embedded member. */
                                            ? <ExternalLink href={link.href ?? ""}>{link.label}</ExternalLink>
                                            : <a href={link.href}>{link.label}</a>}
                                </React.Fragment>)}
                        </span>}
                </span>}
        </nav>
    );
};

export default SurfaceActionBar;
